import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CartContext } from '../../../components/cartContext';
import ThemeToggler from '../../../components/themeToggler';
import menuBrand from '../../../assets/svg-icons/menuBrand.svg';
import projectsFolder from '../../../assets/svg-icons/folder.svg';
import close from '../../../assets/svg-icons/closeBrand.svg';
import arrow from '../../../assets/svg-icons/arrow.svg';
import add from '../../../assets/svg-icons/addWhite.svg';
import { useDispatch, useSelector } from 'react-redux';
import Input from '../../../components/input';
import TextArea from '../../../components/textArea';
import Button from '../../../components/button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  createProject,
  setSelectedProject,
  switchUserProject,
  createWhiteLabel,
  userClearLogOut,
  listUserProjects,
  userLogOut
} from '../../../hooks/local/userReducer';
import Spinner from '../../../components/spinner';
import { usePartner } from '../../../contexts/PartnerContext';
import {
  Bell,
  Mail,
  ChevronDown,
  Plus,
  ShoppingCart,
  Menu,
  SwitchCamera
} from 'lucide-react';
import resources from '../../../assets/svg-icons/resources.svg';
import manage from '../../../assets/svg-icons/edit.svg';
import billing from '../../../assets/svg-icons/blackBilling.svg';
import support from '../../../assets/svg-icons/blackSupport.svg';
import setting from '../../../assets/svg-icons/blackSetting.svg';

const TopBar = ({ toggleMobileMenu }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const userSessionData = useSelector((state) => state.user.userSessionData);

  console.log("userSessionData", userSessionData)

  const navigate = useNavigate();
  const { totalPrice, currency } = useContext(CartContext);
  const {
    switchToAdminMode,
  } = usePartner();

  const [serviceVisible, setServiceVisible] = useState(false);
  const [projectsVisible, setProjectsVisible] = useState(false);
  const [addNewProjectDiv, setAddNewProjectDiv] = useState(false);
  const [createWhiteLabelModal, setCreateWhiteLabelModal] = useState(false);
  const [showPartnerAdminModal, setShowPartnerAdminModal] = useState(false);
  const [projectSwitchingModal, setProjectSwitchingModal] = useState(false);
  const [switchProgress, setSwitchProgress] = useState(0);
  const [switchingProjectName, setSwitchingProjectName] = useState('');

  const userProjectList = useSelector((state) => state.user.userProjectList);
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const loading = useSelector((state) => state.user.loading);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogoutClick = () => {
    setProfileDropdownOpen(false);
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    dispatch(userLogOut());
    dispatch(userClearLogOut());
    navigate('/');
  };

  const handleViewProfile = () => {
    navigate('/profile');
    setProfileDropdownOpen(false);
  };

  useEffect(() => {
    if (userProjectList && userProjectList.length > 0 && !selectedProject) {
      dispatch(setSelectedProject(userProjectList[0]));
    }
  }, [dispatch, userProjectList, selectedProject]);

  const handleProjectSelection = async (userSelectedProject) => {
    // Close the projects list modal
    closeProjects();

    // Show switching modal and set project name
    setSwitchingProjectName(userSelectedProject.name);
    setProjectSwitchingModal(true);
    setSwitchProgress(0);

    // Animate progress bar over 30 seconds
    const progressInterval = setInterval(() => {
      setSwitchProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90; // Hold at 90% until API completes
        }
        return prev + 3; // Increment by 3% every second (30 steps to reach 90%)
      });
    }, 1000);

    try {
      // Update Redux state
      await dispatch(setSelectedProject(userSelectedProject));

      // Call ONLY the switching API
      const { payload } = await dispatch(
        switchUserProject(userSelectedProject.id),
      );

      // Complete progress
      clearInterval(progressInterval);
      setSwitchProgress(100);

      // Wait a moment to show 100% completion
      setTimeout(() => {
        setProjectSwitchingModal(false);

        if (payload.statuscode === '00') {
          const currentPath = location.pathname;
          if (currentPath.includes('manageSecurityGroups')) {
            window.location.href = '/securityGroups';
          } else if (
            currentPath === '/client-area' ||
            currentPath === '/securityGroups' ||
            currentPath === '/configure-vps' ||
            currentPath === '/client-area/volumes'
          ) {
            window.location.reload();
          }
        }
      }, 500);
    } catch (error) {
      clearInterval(progressInterval);
      setProjectSwitchingModal(false);
      toast.error('Failed to switch project');
    }
  };

  const toggleCreateWhiteLabelModal = () => {
    setCreateWhiteLabelModal(!createWhiteLabelModal);
    setServiceVisible(false);
  };

  const closeServices = () => {
    setServiceVisible(false);
  };


  const closeProjects = () => {
    setProjectsVisible(false);
  };

  // Updated function to switch to partner admin using URL params
  const switchToPartnerAdmin = () => {
    switchToAdminMode();
  };

  const createNewProject = useFormik({
    initialValues: {
      projectDescription: '',
      projectName: '',
    },
    validationSchema: Yup.object({
      projectName: Yup.string().required('Project Name cannot be empty'),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { projectName, projectDescription } = values;
      let createProjectData = {
        name: projectName,
        description: projectDescription,
      };
      const { payload } = await dispatch(
        createProject({ createProjectData, navigate }),
      );
      if (payload.statuscode === '00') {
        await dispatch(listUserProjects());
        resetForm();
        setAddNewProjectDiv(false);
        setProjectsVisible(true);
        navigate('/client-area');
      }
    },
  });

  const createWhiteLabelForm = useFormik({
    initialValues: {
      partnerName: '',
      description: '',
      logo: null,
    },
    validationSchema: Yup.object({
      partnerName: Yup.string()
        .required('Partner name is required')
        .matches(
          /^[a-z0-9-]+$/,
          'Partner name must be lowercase, alphanumeric, or hyphens only',
        ),
      description: Yup.string(),
      logo: Yup.mixed()
        .nullable()
        .test('fileSize', 'Logo must be less than 5MB', (value) => {
          if (!value) return true;
          return value.size <= 5 * 1024 * 1024;
        })
        .test('fileType', 'Logo must be PNG, JPG, or JPEG', (value) => {
          if (!value) return true;
          return ['image/png', 'image/jpeg', 'image/jpg'].includes(value.type);
        }),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        setSubmitting(true);

        const formData = new FormData();
        formData.append('name', values.partnerName);
        formData.append('description', values.description);

        if (
          values.logo &&
          values.logo instanceof File &&
          values.logo.size > 0
        ) {
          formData.append('logo', values.logo);
        }

        const { payload } = await dispatch(createWhiteLabel(formData));

        if (payload.statuscode === '00') {
          toast.success('White-label partner created successfully!');
          resetForm();
          setCreateWhiteLabelModal(false);
          window.location.reload();
        } else {
          if (payload.errors) {
            Object.keys(payload.errors).forEach((field) => {
              if (Array.isArray(payload.errors[field])) {
                toast.error(`${field}: ${payload.errors[field][0]}`);
              } else {
                toast.error(`${field}: ${payload.errors[field]}`);
              }
            });
          }
        }
      } catch (error) {
        if (error.response?.data?.errors) {
          Object.keys(error.response.data.errors).forEach((field) => {
            const errorMsg = Array.isArray(error.response.data.errors[field])
              ? error.response.data.errors[field][0]
              : error.response.data.errors[field];
            toast.error(`${field}: ${errorMsg}`);
          });
        } else if (error.response?.data?.statusmessage) {
          toast.error(error.response.data.statusmessage);
        } else if (error.payload?.data?.statusmessage) {
          toast.error(error.payload.data.statusmessage);
        } else {
          toast.error(
            'An error occurred while creating the white-label partner',
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];

    if (file) {
      const isValidType = ['image/png', 'image/jpeg', 'image/jpg'].includes(
        file.type,
      );
      const isValidSize = file.size <= 5 * 1024 * 1024;

      if (!isValidType) {
        toast.error('Please select a PNG, JPG, or JPEG file');
        event.target.value = '';
        return;
      }

      if (!isValidSize) {
        toast.error('File size must be less than 5MB');
        event.target.value = '';
        return;
      }

      createWhiteLabelForm.setFieldValue('logo', file);
    } else {
      createWhiteLabelForm.setFieldValue('logo', null);
    }

    createWhiteLabelForm.setFieldTouched('logo', true);
  };


  // Simplified function using the context function
  const switchToPartner = () => {
    setShowPartnerAdminModal(true);
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/client-area') return 'Dashboard';
    if (path.includes('billing')) return 'Billing';
    if (path.includes('settings')) return 'Settings';
    if (path.includes('help')) return 'Support';
    const parts = path.split('/').filter(Boolean);
    if (parts.length > 0) {
      const last = parts[parts.length - 1];
      return last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' ');
    }
    return 'Dashboard';
  };

  return (
    <div className="relative">
      <Spinner loading={useSelector((state) => state.user).loading} />
      
      <header className="bg-white dark:bg-[#141D26] border-b border-gray-100 dark:border-white/5 h-20 px-8 flex items-center justify-between sticky top-0 z-40 bg-white/80 dark:bg-[#141D26]/80 backdrop-blur-md">
        {/* Left: Section Title */}
        <div className="flex items-center gap-6">
          <div className="lg:hidden mr-2 cursor-pointer p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-full transition-colors" onClick={toggleMobileMenu}>
            <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </div>
          <div className="flex flex-col">
            <h1 className="text-md font-bold text-gray-900 dark:text-white tracking-tight leading-none mb-1">
              {getPageTitle()}
            </h1>
            {/* <div 
              onClick={() => setProjectsVisible(true)}
              className="flex items-center gap-1.5 cursor-pointer group"
            >
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 group-hover:text-primaryBlue transition-colors">
                {selectedProject?.name || 'No Project'}
              </span>
              <ChevronDown className="w-3 h-3 text-gray-400 group-hover:text-primaryBlue transition-all" />
            </div> */}
          </div>
        </div>

        {/* Right Area */}
        <div className="flex items-center gap-4">

          {/* Icons (Mail, Bell, Theme) */}
          <div className="flex items-center gap-1">
            <button className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 rounded-full transition-all relative">
              <Mail className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-[#141D26]"></span>
            </button>
            <button className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 rounded-full transition-all">
              <Bell className="w-4 h-4" />
            </button>
            <ThemeToggler />
          </div>

          <div className="hidden md:block h-6 w-px bg-gray-200 dark:bg-white/10"></div>

          {/* Profile Dropdown Area */}
          <div className="relative">
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}>
               <div className="text-right hidden sm:block">
                  <p className="text-[13px] font-bold text-gray-900 dark:text-white leading-tight">
                    {userSessionData?.fullname || 'User'}
                  </p>
                  <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 leading-tight">
                    {userSessionData?.email || 'Student Account'}
                  </p>
               </div>
               <div className="w-9 h-9 rounded-full bg-primaryBlue text-white flex items-center justify-center text-sm font-bold shadow-sm group-hover:scale-105 transition-transform">
                  {userSessionData?.fullname?.charAt(0) || 'U'}
               </div>
               <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-gray-800 dark:group-hover:text-white transition-colors" />
            </div>

            {profileDropdownOpen && (
              <>
                <div 
                  className="fixed inset-0 z-[9998]" 
                  onClick={() => setProfileDropdownOpen(false)}
                />
                <div className="absolute top-[calc(100%+8px)] right-0 z-[9999] bg-white dark:bg-[#141D26] border border-gray-200 dark:border-white/10 rounded-xl shadow-[0px_0px_32px_4px_#0000000D] w-[299px] overflow-hidden p-5 flex flex-col items-center gap-4">
                  {loading ? (
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-white/10 animate-pulse" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primaryBlue text-white flex items-center justify-center text-lg font-bold shadow-md">
                      {userSessionData?.fullname?.charAt(0) || 'U'}
                    </div>
                  )}
                  
                  <div className="flex flex-col items-center gap-2">
                    <p className="font-bold text-[16px] text-gray-900 dark:text-white leading-[160%]">
                      {loading ? <div className="w-[150px] h-4 bg-gray-200 dark:bg-white/10 animate-pulse rounded" /> : (userSessionData?.fullname || 'N/A')}
                    </p>
                    <p className="font-normal text-[14px] text-gray-500 dark:text-gray-400 leading-[160%]">
                      {loading ? <div className="w-[200px] h-4 bg-gray-200 dark:bg-white/10 animate-pulse rounded" /> : (userSessionData?.email || 'N/A')}
                    </p>
                  </div>

                  <button
                    className="w-full bg-white dark:bg-transparent border border-primaryBlue rounded-full p-2.5 text-[14px] font-medium text-primaryBlue cursor-pointer hover:bg-primaryBlue/5 transition-colors"
                    onClick={handleViewProfile}
                  >
                    View Profile
                  </button>

                  <button
                    className="w-full bg-[#E53E3E] rounded-full p-2.5 text-[14px] font-medium text-white cursor-pointer hover:bg-[#C53030] transition-colors"
                    onClick={handleLogoutClick}
                  >
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Modern Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowLogoutConfirm(false)}
          />
          <div className="relative bg-white dark:bg-[#141D26] rounded-2xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center gap-5 animate-[fadeScaleIn_0.2s_ease-out]">
            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>

            {/* Text */}
            <div className="text-center">
              <h3 className="text-[18px] font-bold text-gray-900 dark:text-white mb-2">Sign out?</h3>
              <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
                You will be logged out of your account. You can sign back in at any time.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-2.5 rounded-xl text-[14px] font-medium bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="flex-1 py-2.5 rounded-xl text-[14px] font-medium bg-red-500 text-white hover:bg-red-600 transition-all hover:shadow-lg hover:shadow-red-500/25"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {showPartnerAdminModal && (
        <div className="w-full fixed h-screen bg-black/70 top-0 px-4 flex items-end md:items-center justify-center">
          <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[40%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto p-4 md:p-6 border-y-[16px] dark:border-y-[#141d26]">
            <div
              onClick={() => setShowPartnerAdminModal(false)}
              className="cursor-pointer mb-4 text-right text-[#c4c4c4]"
            >
              [Close]
            </div>
            <div className="grid gap-4 text-sm mb-6">
              <span className="font-bold text-md">Access Partner Panel</span>
              <span>
                Switch to your partner panel to manage your pricing, view
                analytics, and configure your white-label settings.
              </span>
            </div>
            <div className="flex gap-4">
              <button
                onClick={switchToPartnerAdmin}
                className="py-4 px-10 rounded-md text-white text-sm bg-[#1DA1F2]"
              >
                Go to Partner Panel
              </button>
              <button
                onClick={() => setShowPartnerAdminModal(false)}
                className="py-4 px-10 rounded-md border dark:border-[#c4c4c432] text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {serviceVisible && (
        <div className="w-full fixed h-screen bg-black/70 top-0 px-4 flex items-end md:items-center justify-center">
          <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-1/2 lg:w-2/5 dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
            <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
              <div className="flex gap-2 items-center">
                <img src={menuBrand} alt="" className="h-[18px]" />
                <span>Services</span>
              </div>
              <img
                src={close}
                onClick={closeServices}
                className="h-5 cursor-pointer"
                alt=""
              />
            </div>
            <div className="grid text-xs px-4 pb-4 pt-2">
              <Link
                to={'/client-area/vps'}
                onClick={closeServices}
                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all"
              >
                <span>Create dews</span>
                <span>
                  <img src={arrow} alt="" className="-rotate-90" />
                </span>
              </Link>
              <Link
                to={'/client-area/volumes'}
                onClick={closeServices}
                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all"
              >
                <span>Manage volumes</span>
                <span>
                  <img src={arrow} alt="" className="-rotate-90" />
                </span>
              </Link>
              <Link
                to={'/manageLoadBalancers'}
                onClick={closeServices}
                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all"
              >
                <span>Load balancers</span>
                <span>
                  <img src={arrow} alt="" className="-rotate-90" />
                </span>
              </Link>
              <div
                onClick={toggleCreateWhiteLabelModal}
                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all cursor-pointer"
              >
                <span>White Label</span>
                <span>
                  <img src={arrow} alt="" className="-rotate-90" />
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {projectsVisible && (
        <div className="w-full fixed h-screen bg-black/70 top-0 px-4 flex items-end md:items-center justify-center">
          <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
            <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
              <div className="flex gap-2 items-center">
                <img src={projectsFolder} alt="" className="h-[18px]" />
                <span className="dark:text-white">My projects</span>
              </div>
              <img
                src={close}
                onClick={closeProjects}
                className="h-5 cursor-pointer"
                alt=""
              />
            </div>
            <div className="grid text-xs px-4 pb-4 pt-2">
              {userProjectList &&
                userProjectList.length > 0 &&
                userProjectList.map((val, key) => {
                  return (
                    <div
                      key={key}
                      onClick={() => {
                        handleProjectSelection(val);
                      }}
                      className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all cursor-pointer"
                    >
                      <span>{val.name}</span>
                      <span>
                        <img src={arrow} alt="" className="-rotate-90" />
                      </span>
                    </div>
                  );
                })}

              <div
                onClick={() => {
                  setAddNewProjectDiv(true);
                  setProjectsVisible(false);
                }}
                className="flex items-center justify-between bg-primaryBlue py-4 px-3 mt-4 rounded-md cursor-pointer"
              >
                <span>Create a new project</span>
                <img src={add} alt="" className="h-5" />
              </div>
            </div>
          </div>
        </div>
      )}

      {projectSwitchingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#141D26] rounded-2xl w-full max-w-md mx-4 p-8 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-4 animate-pulse">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Switching Project
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Switching to{' '}
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {switchingProjectName}
                </span>
              </p>
            </div>

            {/* Progress Bar */}
            <div className="space-y-3">
              <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${switchProgress}%` }}
                >
                  <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                </div>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600 dark:text-gray-400">
                  {switchProgress < 100 ? 'Please wait...' : 'Complete!'}
                </span>
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  {Math.round(switchProgress)}%
                </span>
              </div>
            </div>

            {/* Loading Animation */}
            <div className="mt-6 flex justify-center space-x-1">
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: '0ms' }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: '150ms' }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: '300ms' }}
              ></div>
            </div>
          </div>
        </div>
      )}


      {addNewProjectDiv && (
        <div className="fixed top-0 h-screen w-full z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Create a new project</p>
              <span
                onClick={() => {
                  setAddNewProjectDiv(false);
                  setProjectsVisible(true);
                }}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form
              className="gap-y-6 grid"
              onSubmit={createNewProject.handleSubmit}
            >
              <Input
                label={'Project name'}
                type={'text'}
                name={'projectName'}
                value={createNewProject.values.projectName}
                onBlur={createNewProject.handleBlur}
                onChange={createNewProject.handleChange}
                onError={
                  createNewProject.errors.projectName &&
                  createNewProject.touched.projectName
                    ? createNewProject.errors.projectName
                    : null
                }
              />
              <TextArea
                label={'Description'}
                type={'text'}
                noOfRow={'3'}
                name={'projectDescription'}
                value={createNewProject.values.projectDescription}
                onBlur={createNewProject.handleBlur}
                onChange={createNewProject.handleChange}
                onError={
                  createNewProject.errors.projectDescription &&
                  createNewProject.touched.projectDescription
                    ? createNewProject.errors.projectDescription
                    : null
                }
              />
              <div className="flex justify-between items-center gap-2">
                <Button buttonText={'Create Project'} buttonRole={'submit'} />
              </div>
            </form>
          </div>
        </div>
      )}

      {createWhiteLabelModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Create White-Label Partner</p>
              <span
                onClick={() => {
                  setCreateWhiteLabelModal(false);
                  setServiceVisible(true);
                }}
                className="cursor-pointer"
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <form
              className="gap-y-6 grid"
              onSubmit={createWhiteLabelForm.handleSubmit}
            >
              <Input
                label={'Partner Name (Subdomain)'}
                type={'text'}
                name={'partnerName'}
                value={createWhiteLabelForm.values.partnerName}
                onBlur={createWhiteLabelForm.handleBlur}
                onChange={createWhiteLabelForm.handleChange}
                onError={
                  createWhiteLabelForm.errors.partnerName &&
                  createWhiteLabelForm.touched.partnerName
                    ? createWhiteLabelForm.errors.partnerName
                    : null
                }
                placeholder={'e.g., swan'}
              />
              <TextArea
                label={'Description'}
                type={'text'}
                noOfRow={'3'}
                name={'description'}
                value={createWhiteLabelForm.values.description}
                onBlur={createWhiteLabelForm.handleBlur}
                onChange={createWhiteLabelForm.handleChange}
                onError={
                  createWhiteLabelForm.errors.description &&
                  createWhiteLabelForm.touched.description
                    ? createWhiteLabelForm.errors.description
                    : null
                }
              />
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Logo (Optional)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    name="logo"
                    id="logo-upload"
                    className="sr-only"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleFileChange}
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`relative flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                      createWhiteLabelForm.values.logo
                        ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    } ${
                      createWhiteLabelForm.errors.logo &&
                      createWhiteLabelForm.touched.logo
                        ? 'border-red-400 bg-red-50 dark:bg-red-900/20'
                        : ''
                    }`}
                  >
                    {createWhiteLabelForm.values.logo ? (
                      <div className="flex flex-col items-center space-y-2">
                        <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-800 rounded-full">
                          <svg
                            className="w-6 h-6 text-green-600 dark:text-green-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-green-600 dark:text-green-400">
                            {createWhiteLabelForm.values.logo.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {(
                              createWhiteLabelForm.values.logo.size /
                              1024 /
                              1024
                            ).toFixed(2)}{' '}
                            MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            createWhiteLabelForm.setFieldValue('logo', null);
                          }}
                          className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center space-y-2">
                        <div className="flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full">
                          <svg
                            className="w-6 h-6 text-gray-400 dark:text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            PNG, JPG, JPEG up to 5MB
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-blue-500 bg-opacity-10 rounded-xl opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-1 shadow-lg">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {createWhiteLabelForm.values.logo
                            ? 'Change file'
                            : 'Select file'}
                        </p>
                      </div>
                    </div>
                  </label>
                </div>
                {createWhiteLabelForm.errors.logo &&
                  createWhiteLabelForm.touched.logo && (
                    <div className="flex items-center space-x-1 text-red-600 dark:text-red-400">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-sm">
                        {createWhiteLabelForm.errors.logo}
                      </p>
                    </div>
                  )}
              </div>
              <div className="flex justify-between items-center gap-2">
                <Button
                  buttonText={loading ? 'Creating...' : 'Create White-Label'}
                  buttonRole={'submit'}
                  disabled={loading}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopBar;
